import { STORAGE_KEY } from '@/constants';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import type { SalesforceEmbedSession } from '@/utils/salesforceEmbed';

/**
 * Which credential a request goes out with, decided in one place.
 *
 * Two regimes:
 *
 * - **Embed mode** (the Salesforce Account panel). Activated once by the page
 *   that minted the session and held in this frame's JavaScript memory only.
 *   Not localStorage (shared with every marketbetter.ai tab) and not
 *   sessionStorage either: same-origin sibling iframes in one top-level tab
 *   share sessionStorage, so the panel would have replaced the CTI softphone's
 *   credential under it. In embed mode the resolver never falls back to any
 *   other identity: an expired or missing session sends no token at all, the
 *   request 401s, and the page's re-bootstrap replaces the session. It must
 *   never quietly become the Supabase login of whoever else uses this browser.
 *
 * - **App mode**: the interactive Supabase JWT, else the CTI softphone's
 *   ephemeral token (localStorage, legacy), with the localStorage workspace.
 */
export type RequestAuthMode = 'embed' | 'supabase' | 'cti' | null;

export interface RequestAuth {
  token: string | null;
  workspaceId: string | null;
  mode: RequestAuthMode;
}

let embedMode = false;
let embedSession: SalesforceEmbedSession | null = null;

const isLive = (session: SalesforceEmbedSession | null): session is SalesforceEmbedSession => (
  !!session && new Date(session.expiresAt).getTime() > Date.now()
);

/** Enter embed mode for the life of this frame, with the session the backend just minted. */
export const activateEmbedAuth = (session: SalesforceEmbedSession): void => {
  embedMode = true;
  embedSession = session;
};

/** Replace (or, with null, drop) the session while staying in embed mode. */
export const setEmbedAuthSession = (session: SalesforceEmbedSession | null): void => {
  embedSession = session;
};

/** Tests only: back to app mode. */
export const resetEmbedAuthForTests = (): void => {
  embedMode = false;
  embedSession = null;
};

export const normalizeStoredToken = (token: string | null | undefined): string | null => {
  if (!token || token === 'null' || token === 'undefined') return null;
  return token;
};

const readLocal = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const resolveRequestAuth = (): RequestAuth => {
  if (typeof window === 'undefined') return { token: null, workspaceId: null, mode: null };

  if (embedMode) {
    return {
      token: isLive(embedSession) ? embedSession.accessToken : null,
      workspaceId: embedSession ? String(embedSession.vendorId) : null,
      mode: 'embed',
    };
  }

  const supabaseToken = normalizeStoredToken(readLocal(STORAGE_KEY.ACCESS_TOKEN));
  const ctiToken = normalizeStoredToken(readLocal(STORAGE_KEY.CTI_ACCESS_TOKEN));
  const workspaceId = normalizeStoredToken(readLocal(WORKSPACE_STORAGE_KEY));

  if (supabaseToken) return { token: supabaseToken, workspaceId, mode: 'supabase' };
  if (ctiToken) return { token: ctiToken, workspaceId, mode: 'cti' };
  return { token: null, workspaceId, mode: null };
};

/** Sessions that have no Supabase refresh behind them: a 401 is final, never a redirect to /login. */
export const isNonInteractiveAuth = (mode: RequestAuthMode): boolean => mode === 'embed' || mode === 'cti';
